import React, { useCallback, useMemo } from "react";
import { Avatar, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { CopyOutlined, LoadingOutlined } from "@ant-design/icons";
import CopyToClipboard from "react-copy-to-clipboard";
import MarkdownViewer from "../../utils/MarkdownViewer";
import moment from "moment-timezone";

import {
  getNameInitials,
  getAvatarColor,
  showNotification,
} from "../../utils/commonFunctions";
import constants from "../../utils/constants";

const { URL_REGEX } = constants;

const ChatItem = ({ message = {} }) => {
  const loggedInUser = useSelector(({ loggedInUser }) => loggedInUser.data);

  // === Determine Message Meta ===
  const { createdBy, body, canCopy, loading, isUser } = useMemo(() => {
    const createdBy = {};
    let canCopy = false;
    let isUser = false;

    if (message?.role === "user") {
      isUser = true;
      createdBy._id = loggedInUser?._id;
      createdBy.name = `${loggedInUser?.first_name} ${
        loggedInUser?.last_name || ""
      }`.trim();
    } else {
      canCopy = true;
      createdBy._id = "writely-bot";
      createdBy.name = "Writely Bot";
    }

    return {
      createdBy,
      body: message?.content || "",
      canCopy,
      loading: message?.loading,
      isUser,
    };
  }, [message, loggedInUser]);

  const avatarColor = useMemo(() => getAvatarColor(createdBy?._id), [createdBy?._id]);
  const nameInitials = useMemo(() => getNameInitials(createdBy?.name), [createdBy?.name]);

  // === Copy Event ===
  const onCopy = useCallback(() => {
    showNotification("success", "Copied to clipboard");
  }, []);

  // === Handle custom switch message ===
  if (message?.role === "custom-model-switch") {
    return (
      <div
        className="text-center text-blue-600 opacity-60 py-2"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }

  return (
    <div
      className={`flex  ${
        isUser ? "flex-row-reverse" : "flex-row"
      } items-start w-full px-4 md:px-6 lg:px-10 py-4`}
    >
      {/* === Avatar === */}
      <div className="flex-shrink-0">
        <Avatar
          size={36}
          shape="circle"
          style={{ backgroundColor: avatarColor }}
        >
          {nameInitials}
        </Avatar>
      </div>

      {/* === Chat Bubble === */}
      <div
        className={`chat-bubble relative p-4 rounded-2xl  mt-1 shadow-sm full-width-chat-bubble  ${
          isUser
            ? "bg-gray-100 text-black rounded-tr-none ml-2"
            : "bg-gray-100 text-black rounded-tr-none ml-2 px-8"
        }`}
        style={{
          wordBreak: "break-word",
          // whiteSpace: "pre-wrap",
        }}
      >
        {/* === Header (Name + Copy Icon) === */}
        <div className="flex justify-between items-center mb-2 bg-gray-100 ">
          <span className="font-semibold text-sm">{createdBy.name}</span>
          <div className="flex items-center space-x-2">
            {loading && (
              <LoadingOutlined
                style={{ fontSize: 16 }}
                className="text-gray-400 animate-pulse"
              />
            )}
            {!loading && canCopy && (
              <Tooltip title="Copy Text">
                <CopyToClipboard text={body} onCopy={onCopy}>
                  <CopyOutlined className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </CopyToClipboard>
              </Tooltip>
            )}
          </div>
        </div>

        {/* === Message Body === */}
        <div className="text-md leading-relaxed">
          <MarkdownViewer markdownContent={body} />
        </div>

        {/* === Timestamp === */}
        {message?.timestamp && (
          <div className="text-[10px] text-gray-400 mt-2 text-right">
            {moment.utc(message?.timestamp).format("DD-MM-YYYY hh:mm:ss A")}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatItem);
